/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./CascadeSetView'
], (CascadeSetView) => {

	/**
	 * Control cascade views in the filter dock
	 */
	class CascadeSetsController {
		constructor({ cascadeSets, filterItems, getParentElem, populateFilterItems, cascadingFilters }){
			this.cascadeSets = cascadeSets;
			this.filterItems = filterItems;
			this.getParentElem = getParentElem;
			this.populateFilterItems = populateFilterItems;
			this.cascadingFilters = cascadingFilters;

			if (cascadeSets) {
				this.cascadeSets.on('change:addToCascadeSet', this.onAddToCascadeSet, this);
				this.cascadeSets.on('change:removeFromCascadeSet', this.onRemoveFromCascadeSet, this);
				this.cascadeSets.on('change:reorderCascadeSets', this.onReorderCascadeSets, this);
			}
		}

		/**
	 	* Initialize the cascade views
	 	*/
		_getCascadeViews(){
			if(this.cascadeSets && !this.cascadeViews){
				this.cascadeViews = this.cascadeSets.getCascadeSetList()
					.map(cascadeSet => this._getCascadeView(cascadeSet));
			}
			return this.cascadeViews || [];
		}

		/**
	 	* Initialize a cascade view
	 	*/
		_getCascadeView(cascadeSet){
			return new CascadeSetView({
				cascadeSet,
				filterItems: this.filterItems,
				parentCtrl: this
			});
		}

		_findCascadeView(cascadeSet) {
			return this.cascadeViews && this.cascadeViews.find((cascadeView) => cascadeView.cascadeSet === cascadeSet);
		}

		/**
	 	* Event Handler for the addition of a cascade set (or items to a cascadeSet)
		* When adding a new cascade set, clear the view cache and re-render/rebuild it.
	 	*/
		onAddToCascadeSet(){
			this.cascadeViews && this.cascadeViews.forEach((view) => view.remove());
			this.cascadeViews = null;
			return this.renderFilterItems();
		}

		//Return the view if it has a valid cascadeSet (ie: one that hasn't been deleted)
		_validateView(view) {
			return this.cascadeSets.getCascadeSetList().find((cascadeSetToFind) => cascadeSetToFind === view.cascadeSet);
		}

		/**
	 	* Event Handler for the removal of a cascade set (or items from a cascadeSet)
	 	*/
		onRemoveFromCascadeSet(payload){
			const datasourceId = payload.info.value[1];
			const idx = this._getCascadeViews().findIndex(view => view.getDataSourceId() === datasourceId);
			if (idx!==-1) {
				const view = this._getCascadeViews()[idx];
				if (!this._validateView(view)) {
					//If the cascadeSet this view is referencing has been removed, remove the view.
					const view = this._getCascadeViews().splice(idx, 1)[0];
					view.remove();
				}
				return this.renderFilterItems();
			}
		}

		onReorderCascadeSets(payload) {
			const cascadeSetIdsReordered = payload && payload.info && payload.info.value;
			if (this.cascadeViews && cascadeSetIdsReordered && cascadeSetIdsReordered.length) {
				const viewsIncludeReorderedSets = this.cascadeViews.find((view) => {
					return view.getCascadeSet() && cascadeSetIdsReordered.includes(view.getCascadeSet().getId());
				});
				if (viewsIncludeReorderedSets) {
					this.cascadeViews.forEach((view) => view.remove());
					this.cascadeViews = null;
					return this.renderFilterItems();
				}
			}
			return false;
		}

		/**
	 	* Add a filter item to a cascade view
	 	*/
		addToCascadeView(filterItem){
			return this._getCascadeViews()
				.some(view => view.addFilterItem(filterItem));
		}

		renderCascadeViewsAfterFilterItem(filterItem){

			let prevElem = null;
			const itemPosition = {
				dataSourceId:'',
				columnId:''
			};

			if(filterItem){
				itemPosition.dataSourceId = filterItem.getDataSourceId();
				itemPosition.columnId = filterItem.getColumnId();
				prevElem = filterItem.getItemDOMnode();
			}

			const views = this._getCascadeViews()
				.filter(view => {
					const position = view.getCascadeSet().getPosition();
					return itemPosition.dataSourceId === position.dataSourceId && itemPosition.columnId === position.columnId;
				});

			views.forEach(view => {
				if(!prevElem){
					this.getParentElem().prepend(view.getNode());
				}else{
					view.getNode().insertAfter(prevElem);
				}
				prevElem = view.getNode();
			});
			return views.length;
		}

		//Set this flag to keep from re-rendering views when deleting a filter item.
		setIsDeletingFilterItem(flag) {
			this._isDeletingFilterItem = flag;
		}

		renderFilterItems(){
			if (!this._isDeletingFilterItem) {
				return this.populateFilterItems({ isGeneralUpdate: () => true });
			}
		}

		destroy(){
			if (this.cascadeSets) {
				this.cascadeSets.off('change:addToCascadeSet', this.onAddToCascadeSet, this);
				this.cascadeSets.off('change:removeFromCascadeSet', this.onRemoveCascadeSet, this);
				this.cascadeSets.off('change:reorderCascadeSets', this.onReorderCascadeSets, this);
			}
			this.cascadeViews && this.cascadeViews.forEach((view) => view.remove());
			this.cascadeViews = null;
			this.cascadeSets = null;
			this.filterItems = null;
			this.getParentElem = null;
			this.populateFilterItems =null;
		}
	}
	return CascadeSetsController;
});
